/* ============================================================================
   RadioGroup.tsx — boutons radio rectangulaires text-only.
   Sélectionné = fill ink + color paper. Hover = bg paper-deep.
   Si > 4 options → stack en colonne, sinon flex wrap.
   ============================================================================ */

import type { ReactNode } from 'react';

export interface RadioOption<T extends string> {
  value: T;
  label: string;
  /** Optionnel — petit thumb (SVG ou char Unicode) à gauche du label */
  thumb?: ReactNode;
  /** Optionnel — title pour tooltip natif */
  title?: string;
}

interface RadioGroupProps<T extends string> {
  /** Optionnel — label affiché au-dessus du groupe (sinon utiliser <Section> en parent) */
  label?: string;
  options: RadioOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Optionnel — force stack en colonne au lieu de wrap */
  stack?: boolean;
}

export function RadioGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  stack = false,
}: RadioGroupProps<T>) {
  return (
    <div className="controls-radio-wrap">
      {label ? <div className="controls-sublabel">{label}</div> : null}
      <div
        className={`controls-radio-group${stack ? ' controls-radio-group--stack' : ''}`}
        role="radiogroup"
        aria-label={label}
      >
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              type="button"
              key={opt.value}
              className="controls-radio"
              role="radio"
              aria-checked={selected}
              aria-pressed={selected}
              title={opt.title}
              onClick={() => onChange(opt.value)}
            >
              {opt.thumb ? <span className="controls-radio__thumb">{opt.thumb}</span> : null}
              <span className="controls-radio__label">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
